import { Component, OnDestroy } from '@angular/core';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { HttpClient } from '@angular/common/http';
import {
  Lead,
  ModeOfTransport,
  modesOfTransport,
  OpeningTimes,
  RoutingAnswer,
  RoutingRequest,
  RoutingWithOpeningTimesRequest,
  TimePoint,
} from '../../../../lead.model';
import { MatIcon } from '@angular/material/icon';
import { SnackbarService } from '../../../../services/snackbar/snackbar.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LeadService } from '../../../../services/leads/lead.service';
import { LeadRoutingService } from '../../../../services/lead-routing/lead-routing.service';
import { MatDivider } from '@angular/material/divider';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCard, MatCardContent } from '@angular/material/card';
import {
  KeyValue,
  KeyValueTableComponent,
} from '../../../../reusable-components/key-value-table/key-value-table.component';
import { CompressedOpeningTimes } from '../../../../reusable-components/compressed-opening-times';
import { Unsubscribable } from 'rxjs';
import { GeolocationService } from '../../../../services/geolocation/geolocation.service';
import { RouteInformationService } from '../../../route-information.service';
import { RouteSettingsService } from '../../../../route-settings/route-settings.service';

interface Route {
  start: TimePoint;
  keyValues: KeyValue[];
  expanded: boolean;
  subscriptions: Unsubscribable[]; //these have to be unsubscribed, when this element is destroyed
  routeElements: RouteElement[];
}

interface RouteElement {
  arrival: TimePoint;
  keyValues: KeyValue[];
  name: string;
  expanded: boolean;
}

@Component({
  selector: 'app-selected-leads-routing',
  imports: [
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelTitle,
    MatExpansionPanelHeader,
    MatButton,
    MatCheckbox,
    MatIcon,
    MatIconButton,
    TranslatePipe,
    MatDivider,
    MatProgressSpinner,
    MatOption,
    MatSelect,
    MatFormField,
    MatLabel,
    FormsModule,
    MatCard,
    MatCardContent,
    ReactiveFormsModule,
    KeyValueTableComponent,
  ],
  templateUrl: './selected-leads-routing.component.html',
  styleUrl: './selected-leads-routing.component.scss',
})
export class SelectedLeadsRoutingComponent implements OnDestroy {
  modeOfTransport: ModeOfTransport = modesOfTransport[0];

  rout: Route | undefined;

  routeCalculationInProgress = false;

  constructor(
    private readonly leadsService: LeadService,
    protected geolocationService: GeolocationService,
    protected http: HttpClient,
    protected snackbarService: SnackbarService,
    private readonly leadRouting: LeadRoutingService,
    private readonly translateService: TranslateService,
    private readonly routeInformationService: RouteInformationService,
    private readonly routeSettingsService: RouteSettingsService,
  ) {
    routeInformationService.redrawRoute$.subscribe(() => {
      const mapInformation = routeInformationService.getMapInformation();
      if (
        mapInformation.type === 'routeInformation' &&
        mapInformation.routeElements.length > 0
      ) {
        const keyValues: KeyValue[] = [
          {
            key: 'components.selected-leads-routing.mode-of-transport',
            value: 'enums.modeOfTransport.' + mapInformation.modeOfTransport,
            translateValue: true,
          },
        ];
        if (mapInformation.modeOfTransport === 'CAR') {
          keyValues.push({
            key: 'components.route-settings.car-parking-time-label',
            value: mapInformation.overheadPerLeadInMinutes.toString(),
          });
        } else if (mapInformation.modeOfTransport === 'BICYCLE') {
          keyValues.push({
            key: 'components.route-settings.bike-parking-time-label',
            value: mapInformation.overheadPerLeadInMinutes.toString(),
          });
        }
        keyValues.push({
          key: 'components.selected-leads-routing.with-openingTimes-checkbox',
          value:
            'components.selected-leads-routing.route-summary.' +
            (mapInformation.withOpeningTimes ? 'yes' : 'no'),
          translateValue: true,
        });
        keyValues.push({
          key: 'components.route-settings.maximum-tour-length-label',
          value: mapInformation.maximumTourLengthInHours.toString(),
        });
        keyValues.push({
          key: 'components.route-settings.time-per-lead-label',
          value: mapInformation.timePerLeadInMinutes.toString(),
        });

        const subscriptions: Unsubscribable[] = [];
        const route = {
          start: mapInformation.start,
          expanded: false,
          keyValues: keyValues,
          subscriptions: subscriptions,
          routeElements: mapInformation.routeElements.map((routeElement) => {
            const keyValues: KeyValue[] = [];
            if (mapInformation.overheadPerLeadInMinutes > 0) {
              if (mapInformation.modeOfTransport === 'CAR') {
                keyValues.push({
                  key: 'components.selected-leads-routing.route-summary.car-parking-search',
                  value: this.timeToString(routeElement.arrival),
                });
              } else if (mapInformation.modeOfTransport === 'BICYCLE') {
                keyValues.push({
                  key: 'components.selected-leads-routing.route-summary.bike-parking-search',
                  value: this.timeToString(routeElement.arrival),
                });
              }
            }
            keyValues.push({
              key: 'components.selected-leads-routing.route-summary.start-examination',
              value: this.timeToString(routeElement.startExamination),
            });
            keyValues.push({
              key: 'components.selected-leads-routing.route-summary.departure',
              value: this.timeToString(routeElement.departure),
            });

            const keyValue: KeyValue = {
              key: 'components.selected-leads-routing.route-summary.openingTimes',
              value: '',
            };
            const compressedOpeningTimes = new CompressedOpeningTimes(
              this.translateService,
              routeElement.targetLead.openingTimes || { openingDays: [] },
            );
            subscriptions.push(
              compressedOpeningTimes.subscribe(
                (newCompressedOpeningTimes: string) =>
                  (keyValue.value = newCompressedOpeningTimes),
              ),
            );
            keyValues.push(keyValue);

            return {
              arrival: routeElement.arrival,
              name: routeElement.targetLead.name || '',
              keyValues: keyValues,
              expanded: false,
            };
          }),
        };
        this.setRouteSummary(route);
      }
    });
  }

  private setRouteSummary(route: Route | undefined) {
    this.rout?.subscriptions?.forEach((subscription) =>
      subscription.unsubscribe(),
    );
    this.rout = route;
  }

  async calculateRoute(withOpeningTimes: boolean) {
    this.setRouteSummary(undefined);
    const leads = this.leadsService.getSelectedLeads();
    if (leads.length > 50) {
      this.snackbarService.show(
        'components.selected-leads-routing.picked-more-than-fifty-leads-error',
      );
      return;
    }

    const location = await this.geolocationService.getPosition();
    let timePerLeadOverhead = 0;
    if (this.modeOfTransport == 'CAR') {
      timePerLeadOverhead =
        this.routeSettingsService.getCarParkingTimeInMinutes();
    } else if (this.modeOfTransport == 'BICYCLE') {
      timePerLeadOverhead =
        this.routeSettingsService.getBikeParkingTimeInMinutes();
    }

    if (withOpeningTimes) {
      const openingTimes: OpeningTimes[] = leads.map(
        (lead: Lead) => lead.openingTimes || { openingDays: [] },
      );
      const routingRequest: RoutingWithOpeningTimesRequest = {
        maximumTourLengthInHours:
          this.routeSettingsService.getMaximumTourLengthInHours(),
        overheadPerLeadInMinutes: timePerLeadOverhead,
        timePerLeadInMinutes:
          this.routeSettingsService.getTimePerLeadInMinutes(),
        modeOfTransport: this.modeOfTransport,
        userLocation: {
          latitude: location.coords.latitude,
          longitude: location.coords.longitude,
        },
        toVisit: leads.map((lead) => lead.coordinates),
        openingTimes: openingTimes,
      };

      this.routeCalculationInProgress = true;
      this.leadRouting
        .calculateRouteWithOpeningTimes(routingRequest)
        .subscribe({
          next: (response: RoutingAnswer) => {
            this.routeCalculationInProgress = false;
            this.processRoutingResponse(response, withOpeningTimes, leads);
          },
          error: (err) => {
            this.routeCalculationInProgress = false;
            console.error('Fehler:', err);
          },
        });
    } else {
      const routingRequest: RoutingRequest = {
        maximumTourLengthInHours:
          this.routeSettingsService.getMaximumTourLengthInHours(),
        overheadPerLeadInMinutes: timePerLeadOverhead,
        timePerLeadInMinutes:
          this.routeSettingsService.getTimePerLeadInMinutes(),
        modeOfTransport: this.modeOfTransport,
        userLocation: {
          latitude: location.coords.latitude,
          longitude: location.coords.longitude,
        },
        toVisit: leads.map((lead) => lead.coordinates),
      };
      this.routeCalculationInProgress = true;

      this.leadRouting.calculateRoute(routingRequest).subscribe({
        next: (response: RoutingAnswer) => {
          this.routeCalculationInProgress = false;
          this.processRoutingResponse(response, withOpeningTimes, leads);
        },
        error: (err) => {
          this.routeCalculationInProgress = false;
          console.error('Fehler:', err);
        },
      });
    }
  }

  private processRoutingResponse(
    response: RoutingAnswer,
    withOpeningTimes: boolean,
    selectedLeads: Lead[],
  ) {
    this.routeInformationService.setRoutingResponse(
      response,
      selectedLeads,
      withOpeningTimes,
    );
  }

  protected readonly timeToString = (time: TimePoint) =>
    `${time.hour.toString().padStart(2, '0')}:${time.minute.toString().padStart(2, '0')}`;
  protected readonly modesOfTransport = modesOfTransport;

  ngOnDestroy(): void {
    this.rout?.subscriptions.forEach((subscription) =>
      subscription.unsubscribe(),
    );
  }
}
